import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { IconDropDownArrow12pxRegular } from "@pionex-web-kit/icons";
import classNames from "classnames";
import ResizeObserver from "rc-resize-observer";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ConfigContext } from "../../ConfigProvider";
import SizeContext from "../../ConfigProvider/SizeContext";
import useMergedState from "../../hack/rc-util/hooks/useMergedState";
import getDataOrAriaProps from "../../hack/_util/getDataOrAriaProps";
import { useRafState } from "../../hack/_util/hooks/useRaf";
import useSyncState from "../../hack/_util/hooks/useSyncState";
import useRaf from "../../Tabs/hack/rc-tabs/hooks/useRaf";
import useRefs from "../../Tabs/hack/rc-tabs/hooks/useRefs";
import useTouchMove from "../../Tabs/hack/rc-tabs/hooks/useTouchMove";
import { RadioGroupContextProvider } from "./context";
import useOffsets from "./hooks/useOffsets";
import useVisibleRange from "./hooks/useVisibleRange";
import Radio from "./radio";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var RadioGroup = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _React$useContext = React.useContext(ConfigContext),
    getPrefixCls = _React$useContext.getPrefixCls,
    direction = _React$useContext.direction;
  var size = React.useContext(SizeContext);
  var groupWrapperRef = useRef();
  var radioListRef = useRef();
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    wrapperScrollWidth = _useState2[0],
    setWrapperScrollWidth = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    wrapperScrollHeight = _useState4[0],
    setWrapperScrollHeight = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    wrapperWidth = _useState6[0],
    setWrapperWidth = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    wrapperHeight = _useState8[0],
    setWrapperHeight = _useState8[1];
  var _useRefs = useRefs(),
    _useRefs2 = _slicedToArray(_useRefs, 1),
    getBtnRef = _useRefs2[0];
  var _useRafState = useRafState(new Map()),
    _useRafState2 = _slicedToArray(_useRafState, 2),
    tabSizes = _useRafState2[0],
    setTabSizes = _useRafState2[1];
  var memoOptions = useMemo(function () {
    if (props.options && props.options.length > 0) {
      return props.options.map(function (option) {
        if (typeof option === "string" || typeof option === "number") {
          // 此处类型自动推导为 string
          return {
            key: option.toString()
          };
        }
        // 此处类型自动推导为 { label: string value: string }
        return {
          key: "radio-group-value-options-".concat(option.value)
        };
      });
    } else if (Array.isArray(props.children)) {
      return props.children.map(function (node) {
        if ( /*#__PURE__*/React.isValidElement(node)) {
          var _props = node.props;
          return {
            key: _props.key || _props.value
          };
        }
      }).filter(Boolean);
    }
    return [];
  }, [props.options, props.children]);
  var tabOffsets = useOffsets(memoOptions, tabSizes, wrapperScrollWidth);
  var _useSyncState = useSyncState(0, function (next, prev) {}),
    _useSyncState2 = _slicedToArray(_useSyncState, 2),
    transformLeft = _useSyncState2[0],
    setTransformLeft = _useSyncState2[1];
  var _useSyncState3 = useSyncState(0, function (next, prev) {}),
    _useSyncState4 = _slicedToArray(_useSyncState3, 2),
    transformTop = _useSyncState4[0],
    setTransformTop = _useSyncState4[1];
  var _useMergedState = useMergedState(props.defaultValue, {
      value: props.value
    }),
    _useMergedState2 = _slicedToArray(_useMergedState, 2),
    value = _useMergedState2[0],
    setValue = _useMergedState2[1];
  var onRadioChange = function onRadioChange(ev) {
    var lastValue = value;
    // @ts-ignore
    var val = ev.target.value;
    if (!("value" in props)) {
      setValue(val);
    }
    var onChange = props.onChange;
    if (onChange && val !== lastValue) {
      onChange(ev);
    }
  };
  var touchMovingRef = useRef();
  var _useState9 = useState(),
    _useState10 = _slicedToArray(_useState9, 2),
    lockAnimation = _useState10[0],
    setLockAnimation = _useState10[1];
  function doLockAnimation() {
    setLockAnimation(Date.now());
  }
  function clearTouchMoving() {
    window.clearTimeout(touchMovingRef.current);
  }
  var transformMin = 0;
  var transformMax = 0;
  if (direction === "rtl") {
    transformMin = 0;
    transformMax = Math.max(0, wrapperScrollWidth - wrapperWidth);
  } else {
    transformMin = Math.min(0, wrapperWidth - wrapperScrollWidth);
    transformMax = 0;
  }
  function alignInRange(value) {
    if (value < transformMin) {
      return transformMin;
    }
    if (value > transformMax) {
      return transformMax;
    }
    return value;
  }
  useTouchMove(groupWrapperRef, function (offsetX, offsetY) {
    function doMove(setState, offset) {
      setState(function (value) {
        var newValue = alignInRange(value + offset);
        return newValue;
      });
    }
    if (wrapperWidth >= wrapperScrollWidth) {
      return false;
    }
    doMove(setTransformLeft, offsetX);
    clearTouchMoving();
    doLockAnimation();
    return true;
  });
  useEffect(function () {
    clearTouchMoving();
    if (lockAnimation) {
      touchMovingRef.current = window.setTimeout(function () {
        setLockAnimation(0);
      }, 100);
    }
    return clearTouchMoving;
  }, [lockAnimation]);

  // ========================= Scroll ========================
  function scrollToTab(key) {
    var tabOffset = tabOffsets.get(key) || {
      width: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0
    };

    // ============ Align with top & bottom ============
    var newTransform = transformLeft;

    // RTL
    if (direction === "rtl") {
      if (tabOffset.right < transformLeft) {
        newTransform = tabOffset.right;
      } else if (tabOffset.right + tabOffset.width > transformLeft + wrapperWidth) {
        newTransform = tabOffset.right + tabOffset.width - wrapperWidth;
      }
    }
    // LTR
    else if (tabOffset.left < -transformLeft) {
      newTransform = -tabOffset.left;
    } else if (tabOffset.left + tabOffset.width > -transformLeft + wrapperWidth) {
      newTransform = -(tabOffset.left + tabOffset.width - wrapperWidth);
    }
    setTransformTop(0);
    setTransformLeft(alignInRange(newTransform));
  }
  var onListHolderResize = useRaf(function () {
    var _groupWrapperRef$curr, _groupWrapperRef$curr2, _radioListRef$current, _radioListRef$current2;
    // Update wrapper records
    var offsetWidth = ((_groupWrapperRef$curr = groupWrapperRef.current) === null || _groupWrapperRef$curr === void 0 ? void 0 : _groupWrapperRef$curr.offsetWidth) || 0;
    var offsetHeight = ((_groupWrapperRef$curr2 = groupWrapperRef.current) === null || _groupWrapperRef$curr2 === void 0 ? void 0 : _groupWrapperRef$curr2.offsetHeight) || 0;
    setWrapperWidth(offsetWidth);
    setWrapperHeight(offsetHeight);
    var newWrapperScrollWidth = ((_radioListRef$current = radioListRef.current) === null || _radioListRef$current === void 0 ? void 0 : _radioListRef$current.offsetWidth) || 0;
    var newWrapperScrollHeight = ((_radioListRef$current2 = radioListRef.current) === null || _radioListRef$current2 === void 0 ? void 0 : _radioListRef$current2.offsetHeight) || 0;
    setWrapperScrollWidth(newWrapperScrollWidth);
    setWrapperScrollHeight(newWrapperScrollHeight);

    // Update buttons records
    setTabSizes(function () {
      var newSizes = new Map();
      memoOptions.forEach(function (_ref) {
        var _btnNode, _btnNode$wrapRef;
        var key = _ref.key;
        var btnNode = getBtnRef(key).current;
        btnNode = (_btnNode = btnNode) === null || _btnNode === void 0 ? void 0 : (_btnNode$wrapRef = _btnNode.wrapRef) === null || _btnNode$wrapRef === void 0 ? void 0 : _btnNode$wrapRef.current;
        if (btnNode) {
          newSizes.set(key, {
            width: btnNode.offsetWidth,
            height: btnNode.offsetHeight,
            left: btnNode.offsetLeft,
            top: btnNode.offsetTop
          });
        }
      });
      return newSizes;
    });
  });
  var _useVisibleRange = useVisibleRange(tabOffsets, {
      width: wrapperWidth,
      height: wrapperHeight,
      left: transformLeft,
      top: transformTop
    }, {
      width: wrapperScrollWidth,
      height: wrapperScrollHeight
    }, _objectSpread(_objectSpread({}, props), {}, {
      rtl: direction === "rtl",
      list: memoOptions
    })),
    _useVisibleRange2 = _slicedToArray(_useVisibleRange, 2),
    visibleStart = _useVisibleRange2[0],
    visibleEnd = _useVisibleRange2[1];
  var startHiddenTabs = memoOptions.slice(0, visibleStart);
  var endHiddenTabs = memoOptions.slice(visibleEnd + 1);
  var hiddenTabs = [].concat(_toConsumableArray(startHiddenTabs), _toConsumableArray(endHiddenTabs));
  var onStartArrowClick = function onStartArrowClick() {
    if (startHiddenTabs.length) {
      scrollToTab(startHiddenTabs[startHiddenTabs.length - 1].key);
    }
  };
  var onEndArrowClick = function onEndArrowClick() {
    if (endHiddenTabs.length > 0) {
      scrollToTab(endHiddenTabs[0].key);
    }
  };
  var renderGroup = function renderGroup() {
    var _backgroundClassNames, _classNames, _classNames2;
    var customizePrefixCls = props.prefixCls,
      _props$className = props.className,
      className = _props$className === void 0 ? "" : _props$className,
      options = props.options,
      _props$buttonStyle = props.buttonStyle,
      buttonStyle = _props$buttonStyle === void 0 ? "outline" : _props$buttonStyle,
      disabled = props.disabled,
      children = props.children,
      customizeSize = props.size,
      style = props.style,
      id = props.id,
      onMouseEnter = props.onMouseEnter,
      onMouseLeave = props.onMouseLeave,
      moreMode = props.moreMode;
    var prefixCls = getPrefixCls("radio", customizePrefixCls);
    var wrapPrefix = "".concat(prefixCls, "-wrap");
    var groupPrefixCls = "".concat(prefixCls, "-group");
    var pingLeft;
    var pingRight;
    var pingTop;
    var pingBottom;
    if (direction === "rtl") {
      pingRight = transformLeft > 0;
      pingLeft = transformLeft + wrapperWidth < wrapperScrollWidth;
    } else {
      pingLeft = transformLeft < 0;
      pingRight = -transformLeft + wrapperWidth < wrapperScrollWidth;
    }
    var childrenToRender = children;
    // 如果存在 options, 优先使用
    if (options && options.length > 0) {
      childrenToRender = options.map(function (option) {
        if (typeof option === "string" || typeof option === "number") {
          // 此处类型自动推导为 string
          return /*#__PURE__*/_jsx(Radio, {
            prefixCls: prefixCls,
            disabled: disabled,
            value: option,
            checked: value === option,
            children: option
          }, option.toString());
        }
        // 此处类型自动推导为 { label: string value: string }
        return /*#__PURE__*/_jsx(Radio, {
          prefixCls: prefixCls,
          disabled: option.disabled || disabled,
          value: option.value,
          checked: value === option.value,
          style: option.style,
          ref: getBtnRef("radio-group-value-options-".concat(option.value)),
          children: option.label
        }, "radio-group-value-options-".concat(option.value));
      });
    } else {
      // TODO: 未做单个子节点判断，不过应该不影响功能
      if (Array.isArray(props.children)) {
        childrenToRender = props.children.map(function (node) {
          if ( /*#__PURE__*/React.isValidElement(node)) {
            var _props2 = node.props;
            // TODO: 这里覆盖了子组件的ref，会出问题，后续优化
            return /*#__PURE__*/React.cloneElement(node, {
              ref: getBtnRef(_props2.key || _props2.value)
            });
          }
          return node;
        });
      }
    }
    var mergedSize = customizeSize || size;
    var backgroundClassNames = (_backgroundClassNames = {}, _defineProperty(_backgroundClassNames, "".concat(groupPrefixCls, "-joint"), props.bgStyle === "joint"), _defineProperty(_backgroundClassNames, "".concat(groupPrefixCls, "-separate"), props.bgStyle === "separate"), _defineProperty(_backgroundClassNames, "".concat(groupPrefixCls, "-separate-border"), props.bgStyle === "separate-border"), _backgroundClassNames);
    var classString = classNames(groupPrefixCls, "".concat(groupPrefixCls, "-").concat(buttonStyle), (_classNames = {}, _defineProperty(_classNames, "".concat(groupPrefixCls, "-").concat(mergedSize), mergedSize), _defineProperty(_classNames, "".concat(groupPrefixCls, "-rtl"), direction === "rtl"), _defineProperty(_classNames, "".concat(groupPrefixCls, "-slide"), moreMode === "slide"), _classNames), backgroundClassNames, className);
    return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({}, getDataOrAriaProps(props)), {}, {
      className: classString,
      style: style,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      id: id,
      ref: ref,
      children: [moreMode === "slide" && !!pingLeft && /*#__PURE__*/_jsx("div", {
        className: "".concat(prefixCls, "-slide-left-arrow-wrap"),
        onClick: onStartArrowClick,
        children: /*#__PURE__*/_jsx("div", {
          className: "".concat(prefixCls, "-slide-left-arrow"),
          children: /*#__PURE__*/_jsx(IconDropDownArrow12pxRegular, {
            size: 12
          })
        })
      }), moreMode === "slide" ? /*#__PURE__*/_jsx(ResizeObserver, {
        onResize: onListHolderResize,
        children: /*#__PURE__*/_jsx("div", {
          className: classNames(wrapPrefix, (_classNames2 = {}, _defineProperty(_classNames2, "".concat(wrapPrefix, "-ping-left"), pingLeft), _defineProperty(_classNames2, "".concat(wrapPrefix, "-ping-right"), pingRight), _defineProperty(_classNames2, "".concat(wrapPrefix, "-ping-top"), pingTop), _defineProperty(_classNames2, "".concat(wrapPrefix, "-ping-bottom"), pingBottom), _classNames2)),
          ref: groupWrapperRef,
          children: /*#__PURE__*/_jsx(ResizeObserver, {
            onResize: onListHolderResize,
            children: /*#__PURE__*/_jsx("div", {
              ref: radioListRef,
              className: "".concat(prefixCls, "-list"),
              style: {
                transform: "translate(".concat(transformLeft, "px, ").concat(transformTop, "px)"),
                transition: lockAnimation ? "none" : undefined
              },
              children: childrenToRender
            })
          })
        })
      }) : childrenToRender, moreMode === "slide" && !!pingRight && /*#__PURE__*/_jsx("div", {
        className: "".concat(prefixCls, "-slide-right-arrow-wrap"),
        onClick: onEndArrowClick,
        children: /*#__PURE__*/_jsx("div", {
          className: "".concat(prefixCls, "-slide-right-arrow"),
          children: /*#__PURE__*/_jsx(IconDropDownArrow12pxRegular, {
            size: 12
          })
        })
      })]
    }));
  };
  return /*#__PURE__*/_jsx(RadioGroupContextProvider, {
    value: {
      onChange: onRadioChange,
      value: value,
      disabled: props.disabled,
      name: props.name,
      optionType: props.optionType
    },
    children: renderGroup()
  });
});
export default /*#__PURE__*/React.memo(RadioGroup);